// scorecard view-model bits. no react, no i18n — just keys + numbers.
import { SOIL_SIGHT_TILLAGE } from "consts";

import { ResilienceInsightRequest, SoilSightInsightLanguage } from "models";

import { clampScore } from "pages/app/resilience-score/utils/ResilienceScoreUtils";
import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { growsCoverCrop } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { ScenarioResilienceResult } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

const MAX_SCORE = 10;
const clampPct = (value: number) => Math.max(0, Math.min(100, value));
const clampDeltaVsBaseline = (value: number) => Math.max(-MAX_SCORE, Math.min(MAX_SCORE, Number.isFinite(value) ? value : 0));
const clampCount = (value: number) => Math.max(0, Math.round(Number.isFinite(value) ? value : 0));

export type ResilienceTierKey = "strong" | "good" | "developing" | "needsAttention" | "critical";

export type ResilienceTier = {
  key: ResilienceTierKey;
  labelKey: string;
  interpretationKey: string;
};

// first band that clears `min` wins (inclusive lower edge)
const TIER_BANDS: { min: number; key: ResilienceTierKey }[] = [
  { min: 8, key: "strong" },
  { min: 6, key: "good" },
  { min: 4, key: "developing" },
  { min: 2, key: "needsAttention" },
  { min: 0, key: "critical" },
];

/** Canonical tier order, best to worst — single source of truth for anything listing every tier. */
export const RESILIENCE_TIER_KEYS: ResilienceTierKey[] = TIER_BANDS.map((band) => band.key);

export function getResilienceTier(score: number): ResilienceTier {
  const band = TIER_BANDS.find((b) => score >= b.min) ?? TIER_BANDS[TIER_BANDS.length - 1];
  return {
    key: band.key,
    labelKey: `resilienceScorecard.tier.${band.key}.label`,
    interpretationKey: `resilienceScorecard.tier.${band.key}.interpretation`,
  };
}

export type ResilienceDeltaTone = "positive" | "negative";

export type ResilienceDelta = {
  value: number;
  formatted: string;
  tone: ResilienceDeltaTone;
};

/**
 * delta = scenario.overall − baseline.overall (1 decimal). Returns `null` when the rounded
 * delta is zero (baseline itself, or a scenario tied with baseline) so the badge is omitted.
 * i.e: scenario − baseline. null = no badge
 */
export function getResilienceDelta(scenarioBaseScore: number, baselineBaseScore: number): ResilienceDelta | null {
  const value = Math.round((scenarioBaseScore - baselineBaseScore) * 10) / 10;
  if (value === 0) return null;
  return {
    value,
    formatted: `${value > 0 ? "+" : ""}${value.toFixed(1)}`,
    tone: value > 0 ? "positive" : "negative",
  };
}

/** clamp score to 0–10 first — round(10.05 × 10) would otherwise overflow to 101%. */
export function soilCoverPercentFromScore(soilCoverScore: number): number {
  return Math.round(clampScore(soilCoverScore) * 10);
}

export function scoreToFraction(value: number, max = 10): number {
  return Math.max(0, Math.min(1, value / max));
}

/** Count of filled rotation slots (not distinct crops) — the same basis the API uses to decide whether a rotation is scorable at all. */
export function countFilledCropYears(years: CropYear[] | undefined): number {
  return years?.filter((year) => year.crop).length ?? 0;
}

/** API distinct count when scored; else filled rotation slots (not distinct). */
export function resolveInsightCropCount(result: Pick<ScenarioResilienceResult, "cropsInRotation"> | undefined, years: CropYear[] | undefined): number {
  return result?.cropsInRotation ?? countFilledCropYears(years);
}

export const toInsightLocale = (lng: string): SoilSightInsightLanguage => (lng.startsWith("de") ? "de" : lng.startsWith("pl") ? "pl" : "en");

const tillageLabel = (tillageId: CropYear["tillageId"]): ResilienceInsightRequest["cropRotation"][number]["tillage"] => {
  if (tillageId === SOIL_SIGHT_TILLAGE.CONVENTIONAL) return "conventional";
  if (tillageId === SOIL_SIGHT_TILLAGE.REDUCED) return "reduced";
  return "none";
};

export function buildResilienceInsightRequest(
  years: CropYear[],
  result: ScenarioResilienceResult,
  isBaseline: boolean,
  locale: SoilSightInsightLanguage,
  deltaVsBaseline?: number
): ResilienceInsightRequest {
  return {
    locale,
    isBaseline,
    cropRotation: years
      .filter((year) => year.crop)
      .map((year) => ({
        mainCrop: year.crop,
        coverCrop: growsCoverCrop(year),
        tillage: tillageLabel(year.tillageId),
        ...(year.residue == null ? {} : { residueManagement: year.residue ? ("left-on-field" as const) : ("removed" as const) }),
      })),
    overall: clampScore(result.baseScore),
    soilHealth: clampScore(result.breakdown.soilHealth),
    waterManagement: clampScore(result.breakdown.waterCapacity),
    biodiversity: clampScore(result.breakdown.biodiversity),
    ...(deltaVsBaseline == null ? {} : { deltaVsBaseline: clampDeltaVsBaseline(deltaVsBaseline) }),
    cropCount: clampCount(resolveInsightCropCount(result, years)),
    plantFamilies: clampCount(result.plantFamilies ?? 0),
    soilCoverPct: result.soilCoverScore != null ? soilCoverPercentFromScore(result.soilCoverScore) : 0,
    coverCropPct: clampPct(result.coverCropSharePercent ?? 0),
  };
}

// weights match API + insight prompt priority
const SUBSCORE_WEIGHTS = { soilHealth: 0.4, waterManagement: 0.4, biodiversity: 0.2 } as const;
const STRONG_DRIVER_THRESHOLD = 7.5;

export type ResilienceLeverKey = "coverCrop" | "soilHealth" | "waterManagement" | "biodiversity" | "maintain";

// offline fallback .. same priority as AI prompt → i18n: resilienceScorecard.topOpportunity.*
export function topOpportunity(result: ScenarioResilienceResult): ResilienceLeverKey {
  if ((result.coverCropSharePercent ?? 0) === 0) return "coverCrop";

  const drivers = [
    { key: "soilHealth" as const, score: result.breakdown.soilHealth, weight: SUBSCORE_WEIGHTS.soilHealth },
    { key: "waterManagement" as const, score: result.breakdown.waterCapacity, weight: SUBSCORE_WEIGHTS.waterManagement },
    { key: "biodiversity" as const, score: result.breakdown.biodiversity, weight: SUBSCORE_WEIGHTS.biodiversity },
  ];
  if (drivers.every((driver) => driver.score >= STRONG_DRIVER_THRESHOLD)) return "maintain";

  // heavier weight wins ties (40% driver beats biodiversity)
  return drivers.reduce((lowest, driver) => (driver.score < lowest.score || (driver.score === lowest.score && driver.weight > lowest.weight) ? driver : lowest))
    .key;
}
